// File: src/components/PostEventFeedbackSheet.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AnimatePresence,
  motion,
} from "framer-motion";
import {
  LuArrowRight,
  LuMessageCircle,
  LuUsersRound,
  LuX,
} from "react-icons/lu";

import { ReactComponent as TribeLogo } from "../assets/TribeLogo.svg";


const COLORS = {
  background: "#ffffff",
  text: "#0b0b0b",
  subtext: "#686868",
  border: "#ECECEE",
  softBackground: "#F6F6F7",
  starActive: "#F4C542",
  starInactive: "#E7E8EA",
};

const CONNECTION_TYPE_SPEED_MS = 34;
const CONNECTION_DELETE_SPEED_MS = 18;
const CONNECTION_HOLD_MS = 900;

function getProfilePhoto(person) {
  if (typeof person === "string") {
    return person.trim();
  }

  return String(
    person?.profilePicUrl ||
      person?.profilePictureUrl ||
      person?.avatarUrl ||
      person?.photoUrl ||
      person?.imageUrl ||
      ""
  ).trim();
}

function getPersonId(person, index) {
  if (typeof person === "string") {
    return `${person}-${index}`;
  }

  return String(
    person?._id ||
      person?.id ||
      person?.userId ||
      index
  );
}

function renderTypedSegments(visibleText, segments) {
  let remaining = visibleText.length;

  return segments.map((segment, index) => {
    if (remaining <= 0) {
      return null;
    }

    const visibleSegment = segment.text.slice(
      0,
      remaining
    );

    remaining -= segment.text.length;

    return (
      <span
        key={`${segment.text}-${index}`}
        style={segment.style}
      >
        {visibleSegment}
      </span>
    );
  });
}

function isPWA() {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    window.matchMedia?.(
      "(display-mode: standalone)"
    ).matches ||
      window.navigator.standalone ===
        true
  );
}

export default function PostEventFeedbackSheet({
  isOpen,
  eventId,
  eventName = "",

  /**
   * Pass the other attendees from the gathering.
   * Each item may be a user object or direct image URL.
   */
  attendees = [],

  onClose,
  onSubmit,
  onOpenChats,

  isSubmitting = false,
}) {
  const [step, setStep] =
    useState("feedback");

  const [rating, setRating] =
    useState(0);

  const [
    anythingElse,
    setAnythingElse,
  ] = useState("");

  
  const [
    submittingInternally,
    setSubmittingInternally,
  ] = useState(false);

  const [
    submissionError,
    setSubmissionError,
  ] = useState("");

  const [
    connectionText,
    setConnectionText,
  ] = useState("");

  const [
    connectionMessageIndex,
    setConnectionMessageIndex,
  ] = useState(0);

 const [
  connectionAnimationComplete,
  setConnectionAnimationComplete,
] = useState(false);

const [
  connectionTextComplete,
  setConnectionTextComplete,
] = useState(false);

  const overlayLockRef = useRef({
    didLock: false,
    y: 0,
    previousTheme: "#ffffff",
    previousBodyOverflow: "",
    previousHtmlOverflow: "",
  });

  const starsWrapRef = useRef(null);
const ratingDragRef = useRef(false);

  /*
   * App.js and the pending-feedback endpoint decide
   * whether this sequence should be open. Keeping one
   * source of truth prevents the UI from silently
   * suppressing an eligible event.
   */
  const shouldShow = Boolean(isOpen);

  const submitting =
    isSubmitting ||
    submittingInternally;

  const canContinue =
    rating > 0 && !submitting;

  const visibleAttendees = useMemo(
    () =>
      (Array.isArray(attendees)
        ? attendees
        : []
      )
        .map((person, index) => ({
          id: getPersonId(
            person,
            index
          ),
          photoUrl:
            getProfilePhoto(person),
        }))
        .filter(
          (person) => person.photoUrl
        )
        .slice(0, 5),
    [attendees]
  );

  const connectionEventName =
    eventName || "this gathering";

  const connectionMessageSegments =
    connectionMessageIndex === 0
      ? [
          {
            text: "Everyone you met at ",
            style: styles.connectionTextLight,
          },
          {
            text: connectionEventName,
            style: styles.connectionTextStrong,
          },
          {
            text: " is now in your ",
            style: styles.connectionTextLight,
          },
          {
            text: "chats page.",
            style: styles.connectionTextStrong,
          },
        ]
      : [
          {
            text: "Send friend requests",
            style: styles.connectionTextStrong,
          },
          {
            text: " to continue your  ",
            style: styles.connectionTextLight,
          },
          {
            text: "conversations.",
            style: styles.connectionTextStrong,
          },
        ];

  useEffect(() => {
    if (!shouldShow) {
      return;
    }

    setStep("feedback");
    setRating(0);
    setAnythingElse("");
    setSubmittingInternally(false);
    setSubmissionError("");
  }, [
    shouldShow,
    eventId,
  ]);

useEffect(() => {
  if (step !== "connections") {
    setConnectionText("");
    setConnectionMessageIndex(0);
    setConnectionTextComplete(false);
    setConnectionAnimationComplete(false);

    return undefined;
  }

  const firstMessage = `Everyone you met at ${
    eventName || "this gathering"
  } is now in your chats page.`;

  const secondMessage =
    "Send a friend request to your connections to continue the conversation.";

  let cancelled = false;
  let timer;

  const wait = (delay) =>
    new Promise((resolve) => {
      timer = window.setTimeout(
        resolve,
        delay
      );
    });

  async function runSequence() {
    setConnectionText("");
    setConnectionMessageIndex(0);
    setConnectionTextComplete(false);
    setConnectionAnimationComplete(false);

    /*
     * Type the first message.
     */
    for (
      let index = 1;
      index <= firstMessage.length;
      index += 1
    ) {
      if (cancelled) {
        return;
      }

      setConnectionText(
        firstMessage.slice(0, index)
      );

      await wait(
        CONNECTION_TYPE_SPEED_MS
      );
    }

    /*
     * Hold the first message before deleting it.
     */
    await wait(CONNECTION_HOLD_MS);

    /*
     * Delete the first message.
     */
    for (
      let index =
        firstMessage.length - 1;
      index >= 0;
      index -= 1
    ) {
      if (cancelled) {
        return;
      }

      setConnectionText(
        firstMessage.slice(0, index)
      );

      await wait(
        CONNECTION_DELETE_SPEED_MS
      );
    }

    setConnectionMessageIndex(1);

    /*
     * Type the final message.
     */
    for (
      let index = 1;
      index <= secondMessage.length;
      index += 1
    ) {
      if (cancelled) {
        return;
      }

      setConnectionText(
        secondMessage.slice(0, index)
      );

      await wait(
        CONNECTION_TYPE_SPEED_MS
      );
    }

    /*
     * Only now allow the graphic to mount.
     * The button remains disabled.
     */
    if (!cancelled) {
      setConnectionTextComplete(true);
    }
  }

  runSequence();

  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}, [step, eventName]);

  useEffect(() => {
    if (!shouldShow) {
      return undefined;
    }

    const meta = document.querySelector(
      'meta[name="theme-color"]'
    );

    const alreadyLocked =
      document.body.classList.contains(
        "overlay-active"
      ) ||
      document.body.style.position ===
        "fixed";

    const y =
      window.scrollY ||
      window.pageYOffset ||
      0;

    overlayLockRef.current = {
      didLock: !alreadyLocked,
      y,
      previousTheme:
        meta?.content || "#ffffff",
      previousBodyOverflow:
        document.body.style.overflow,
      previousHtmlOverflow:
        document.documentElement.style
          .overflow,
    };

    if (meta) {
      meta.content = "#ffffff";
    }

    if (!alreadyLocked) {
      document.body.style.top = `-${y}px`;

      document.body.classList.add(
        "overlay-active"
      );
    }

    document.body.style.overflow =
      "hidden";

    document.documentElement.style.overflow =
      "hidden";

    return () => {
      if (meta) {
        meta.content =
          overlayLockRef.current
            .previousTheme ||
          "#ffffff";
      }

      document.body.style.overflow =
        overlayLockRef.current
          .previousBodyOverflow;

      document.documentElement.style.overflow =
        overlayLockRef.current
          .previousHtmlOverflow;

      if (
        overlayLockRef.current.didLock
      ) {
        const previousY =
          overlayLockRef.current.y || 0;

        document.body.classList.remove(
          "overlay-active"
        );

        document.body.style.top = "";

        window.scrollTo(
          0,
          previousY
        );
      }
    };
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  function handleClose() {
    if (submitting) {
      return;
    }

    onClose?.();
  }

function handleContinue() {
  if (!canContinue) {
    return;
  }

  setSubmissionError("");
  setStep("connections");
}

async function handleOpenChats() {
  if (submitting) {
    return;
  }

  setSubmissionError("");
  setSubmittingInternally(true);

  try {
    await onSubmit?.({
      eventId,
      rating,
      anythingElse: anythingElse.trim(),
    });

    if (typeof onOpenChats === "function") {
      onOpenChats();
      return;
    }

    onClose?.();
  } catch (error) {
    console.error(
      "[PostEventFeedbackSheet] Could not submit feedback:",
      error?.response?.data ||
        error?.message ||
        error
    );

    setSubmissionError(
      "We couldn’t save your feedback. Please try again."
    );

    setStep("feedback");
  } finally {
    setSubmittingInternally(false);
  }
}

  function updateRatingFromPointer(clientX) {
  if (submitting || !starsWrapRef.current) {
    return;
  }

  const starButtons = Array.from(
    starsWrapRef.current.querySelectorAll(
      "[data-rating-value]"
    )
  );

  if (!starButtons.length) {
    return;
  }

  let closestRating = 1;
  let closestDistance = Infinity;

  starButtons.forEach((button) => {
    const rect =
      button.getBoundingClientRect();

    const center =
      rect.left + rect.width / 2;

    const distance = Math.abs(
      clientX - center
    );

    if (distance < closestDistance) {
      closestDistance = distance;
      closestRating = Number(
        button.dataset.ratingValue
      );
    }
  });

  setRating(closestRating);
  setSubmissionError("");
}

function handleRatingPointerDown(event) {
  if (submitting) {
    return;
  }

  event.preventDefault();

  ratingDragRef.current = true;

  event.currentTarget.setPointerCapture?.(
    event.pointerId
  );

  updateRatingFromPointer(
    event.clientX
  );
}

function handleRatingPointerMove(event) {
  if (!ratingDragRef.current) {
    return;
  }

  event.preventDefault();

  updateRatingFromPointer(
    event.clientX
  );
}

function handleRatingPointerEnd(event) {
  ratingDragRef.current = false;

  if (
    event.currentTarget.hasPointerCapture?.(
      event.pointerId
    )
  ) {
    event.currentTarget.releasePointerCapture(
      event.pointerId
    );
  }
}

  function renderStar(index) {
  const filled = index <= rating;
  const selected = index === rating;

  return (
    <motion.button
      key={index}
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${index} star${
        index === 1 ? "" : "s"
      }`}
      data-rating-value={index}
      disabled={submitting}
      onClick={() => {
        if (submitting) {
          return;
        }

        setRating(index);
        setSubmissionError("");
      }}
      initial={{
        opacity: 0,
        y: 20,
        scale: 0.72,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: selected ? 1.08 : 1,
      }}
      whileTap={{
        scale: 0.9,
      }}
      transition={{
        opacity: {
          delay:
            0.16 + index * 0.055,
          duration: 0.26,
        },
        y: {
          delay:
            0.16 + index * 0.055,
          type: "spring",
          stiffness: 320,
          damping: 22,
        },
        scale: {
          type: "spring",
          stiffness: 410,
          damping: 20,
        },
      }}
      style={styles.starButton}
    >
      <motion.svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        animate={{
          rotate:
            selected && rating > 0
              ? [0, -8, 5, 0]
              : 0,
        }}
        transition={{
          duration: 0.38,
        }}
        style={styles.starIcon}
      >
        <motion.path
          d="M12 2.75l2.93 5.94 6.56.95-4.74 4.62 1.12 6.53L12 17.73l-5.87 3.08 1.12-6.53-4.74-4.62 6.56-.95L12 2.75z"
          animate={{
            fill: filled
              ? COLORS.starActive
              : COLORS.starInactive,
          }}
          transition={{
            duration: 0.16,
            ease: "easeOut",
          }}
        />
      </motion.svg>
    </motion.button>
  );
}

  return (
    <AnimatePresence>
      <motion.section
        key={`post-event-sequence-${eventId}`}
        role="dialog"
        aria-modal="true"
        aria-label="Post-event experience"
        initial={{
          opacity: 0,
          y: 16,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          y: 14,
        }}
        transition={{
          duration: 0.28,
          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        }}
        style={styles.shell}
      >
        <motion.div
          initial={{
            opacity: 0,
            y: -8,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.35,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
          style={styles.topLogo}
        >
          <TribeLogo
            style={
              styles.topLogoSvg
            }
          />
        </motion.div>

       

        <main style={styles.main}>
          <AnimatePresence
            mode="wait"
            initial={false}
          >
            {step === "feedback" ? (
              <motion.div
                key="feedback-step"
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -14,
                }}
                transition={{
                  duration: 0.34,
                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}
                style={
                  styles.feedbackStep
                }
              >
           
                <h1
                  style={
                    styles.feedbackHeading
                  }
                >
                  How was the
                  <br />

                  <span
                    style={
                      styles.headingStrong
                    }
                  >
                    gathering?
                  </span>
                </h1>

                <p
                  style={
                    styles.supportingText
                  }
                >
                  Your feedback helps us
                  make the next one even
                  better.
                </p>

                {eventName && (
                  <div
                    style={
                      styles.eventNamePill
                    }
                  >
                    {eventName}
                  </div>
                )}

            <div
  ref={starsWrapRef}
  role="radiogroup"
  aria-label="Choose a rating"
  onPointerDown={
    handleRatingPointerDown
  }
  onPointerMove={
    handleRatingPointerMove
  }
  onPointerUp={
    handleRatingPointerEnd
  }
  onPointerCancel={
    handleRatingPointerEnd
  }
  style={styles.starsWrap}
>
  {[1, 2, 3, 4, 5].map(
    renderStar
  )}
</div>
                <div
                  style={
                    styles.ratingLabelWrap
                  }
                >
                  <AnimatePresence
                    mode="wait"
                  >
                    {rating > 0 ? (
                      <motion.div
                        key={rating}
                        initial={{
                          opacity: 0,
                          y: 5,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        exit={{
                          opacity: 0,
                          y: -4,
                        }}
                        style={
                          styles.ratingLabel
                        }
                      >
                        {rating === 1
                          ? "Not great"
                          : rating === 2
                            ? "Could be better"
                            : rating === 3
                              ? "It was okay"
                              : rating === 4
                                ? "Really good"
                                : "Loved it"}
                      </motion.div>
                    ) : (
                      <motion.div
                        key="rating-hint"
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        style={
                          styles.ratingHint
                        }
                      >
                        Tap a star to rate
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

          <textarea
  value={anythingElse}
  onChange={(event) =>
    setAnythingElse(
      event.target.value
    )
  }
  placeholder="Anything else you want us to know? Optional"
  rows={4}
  maxLength={1000}
  style={styles.textarea}
/>

<div
  aria-hidden="true"
  style={styles.stepDots}
>
  <motion.div
    animate={{
      width:
        step === "feedback"
          ? 22
          : 6,
      opacity:
        step === "feedback"
          ? 1
          : 0.28,
    }}
    style={styles.stepDot}
  />

  <motion.div
    animate={{
      width:
        step === "connections"
          ? 22
          : 6,
      opacity:
        step === "connections"
          ? 1
          : 0.28,
    }}
    style={styles.stepDot}
  />
</div>

<AnimatePresence>
                  {submissionError ? (
                    <motion.div
                      role="alert"
                      initial={{
                        opacity: 0,
                        y: 4,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: -3,
                      }}
                      style={
                        styles.submissionError
                      }
                    >
                      {submissionError}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div
                key="connections-step"
                initial={{
                  opacity: 0,
                  y: 18,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -14,
                }}
                transition={{
                  duration: 0.38,
                  ease: [
                    0.22,
                    1,
                    0.36,
                    1,
                  ],
                }}
                style={
                  styles.connectionsStep
                }
              >
         
<AnimatePresence initial={false}>
  {connectionTextComplete ? (
    <motion.div
      key="connections-video-reveal"
      initial={{
        height: 0,
        marginBottom: 0,
        opacity: 0,
        scale: 0.92,
        y: 12,
      }}
      animate={{
        height: 200,
        marginBottom: 20,
        opacity: 1,
        scale: 1,
        y: 0,
      }}
      transition={{
        height: {
          duration: 0.52,
          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        },
        marginBottom: {
          duration: 0.52,
          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        },
        opacity: {
          duration: 0.34,
          delay: 0.08,
          ease: "easeOut",
        },
        scale: {
          duration: 0.5,
          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        },
        y: {
          duration: 0.5,
          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        },
      }}
      onAnimationComplete={() => {
        setConnectionAnimationComplete(
          true
        );
      }}
      style={{
        ...styles.connectionsVideoWrap,
        overflow: "hidden",
      }}
    >
      <video
        key="connections-video"
        src="https://res.cloudinary.com/dy7dovjqb/video/upload/v1784304093/enhanced_quality.mp4_womisg.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        style={
          styles.connectionsVideo
        }
        onCanPlay={(event) => {
          event.currentTarget
            .play()
            .catch(() => {});
        }}
      />
    </motion.div>
  ) : null}
</AnimatePresence>

<h1
  style={
    styles.connectionsHeading
  }
>
  {renderTypedSegments(
    connectionText,
    connectionMessageSegments
  )}

  <span
    aria-hidden="true"
    style={
      styles.typewriterCursor
    }
  >
    |
  </span>
</h1>

<div
  aria-hidden="true"
  style={styles.stepDots}
>
  <motion.div
    animate={{
      width: 6,
      opacity: 0.28,
    }}
    style={styles.stepDot}
  />

  <motion.div
    animate={{
      width: 22,
      opacity: 1,
    }}
    style={styles.stepDot}
  />
</div>

      
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <footer
          style={{
            ...styles.footer,
            paddingBottom: `calc(${
              isPWA() ? 18 : 12
            }px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
       

          <button
            type="button"
            disabled={
  step === "feedback"
    ? !canContinue
    : submitting ||
      !connectionAnimationComplete
}
            onClick={
              step === "feedback"
                ? handleContinue
                : handleOpenChats
            }
            style={{
              ...styles.primaryButton,
              ...(
                (step === "feedback" &&
                  !canContinue) ||
                (step === "connections" &&
                  (submitting ||
                    !connectionAnimationComplete))
                  ? styles.primaryButtonDisabled
                  : {}
              ),
            }}
          >
          <span>
  {step === "feedback"
    ? "Continue"
    : "See your connections"}
</span>

            {step ===
              "connections" && (
              <LuArrowRight
                size={18}
                strokeWidth={2.2}
                aria-hidden="true"
              />
            )}
          </button>
        </footer>

        <style>
          {`
            button,
            textarea {
              -webkit-tap-highlight-color: transparent;
            }

            button:focus-visible,
            textarea:focus-visible {
              outline: none;
            }

            textarea::placeholder {
              color: #A1A1AA;
            }

            @keyframes postEventTypewriterCursor {
              0%,
              45% {
                opacity: 1;
              }

              46%,
              100% {
                opacity: 0;
              }
            }

            @media (prefers-reduced-motion: reduce) {
              * {
                animation: none !important;
                transition: none !important;
              }
            }
          `}
        </style>
      </motion.section>
    </AnimatePresence>
  );
}


const styles = {
  shell: {
    position: "fixed",
    inset: 0,
    zIndex: 10000,
    width: "100%",
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    background:
      COLORS.background,
    color: COLORS.text,
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  topLogo: {
  position: "absolute",
  top:
    "calc(20px + env(safe-area-inset-top, 0px))",
  left: "40%",
  zIndex: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  transform: "translateX(-50%)",
},

feedbackStep: {
  width: "100%",
  maxWidth: 660,
  minHeight: "100%",
  margin: "0 auto",
  padding:
    "calc(clamp(150px, 18vh, 185px) + env(safe-area-inset-top, 0px)) 22px 24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  textAlign: "center",
  boxSizing: "border-box",
},


  topLogoSvg: {
    width: 94,
    height: "auto",
    display: "block",
  },



  main: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },

  feedbackStep: {
    width: "100%",
    maxWidth: 660,
    minHeight: "100%",
    margin: "0 auto",
    padding: "0px 22px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
  },

  connectionsStep: {
    width: "100%",
    maxWidth: 700,
    minHeight: "100%",
    margin: "0 auto",
    padding: "0px 22px 38px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
  },

  eyebrow: {
    marginBottom: 0,
    color: "#777777",
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "0.11em",
  },

feedbackHeading: {
  margin: "80px 0 0",
  color: "#292929",
  fontSize:
         "clamp(2.35rem, 8vw, 5rem)",
  lineHeight: 1,
  fontWeight: 520,
  letterSpacing: "-0.05em",
},

  connectionsVideoWrap: {
  width: 200,
  height: 200,
  marginBottom: 20,
  flexShrink: 0,
  overflow: "visible",
  background: "transparent",
},

connectionsVideo: {
  width: "100%",
  height: "100%",
  display: "block",
  objectFit: "cover",
  objectPosition: "center 40%",
  background: "transparent",
  pointerEvents: "none",
},

  connectionsHeading: {
    width: "100%",
    maxWidth: 680,
    minHeight: "2.05em",
    margin: 0,
    color: "#292929",
    fontSize:
      "clamp(2.35rem, 8vw, 5rem)",
    lineHeight: 1,
    fontWeight: 520,
    letterSpacing: "-0.05em",
  },

  connectionTextLight: {
    color: "#292929",
    fontWeight: 320,
  },

  connectionTextStrong: {
    color: COLORS.text,
    fontWeight: 700,
  },

  typewriterCursor: {
    display: "inline-block",
    marginLeft: 3,
    fontWeight: 400,
    animation:
      "postEventTypewriterCursor 0.8s steps(1) infinite",
  },

  headingStrong: {
    color: COLORS.text,
    fontWeight: 900,
  },

  supportingText: {
    maxWidth: 380,
    margin: "17px auto 0",
    color: COLORS.subtext,
    fontSize: 15,
    lineHeight: 1.48,
    fontWeight: 500,
    letterSpacing: "-0.02em",
  },

  eventNamePill: {
    maxWidth: 370,
    marginTop: 18,
    padding: "8px 13px",
    overflow: "hidden",
    borderRadius: 999,
    background:
      COLORS.softBackground,
    color: "#444444",
    fontSize: 12.5,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: "-0.015em",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  starsWrap: {
  marginTop: 34,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "clamp(4px, 1.8vw, 10px)",
  touchAction: "none",
  userSelect: "none",
},
  starButton: {
    width:
      "clamp(47px, 12.5vw, 64px)",
    height:
      "clamp(47px, 12.5vw, 64px)",
    padding: 0,
    display: "grid",
    placeItems: "center",
    border: 0,
    borderRadius: 14,
    background: "transparent",
    cursor: "pointer",
  },

  starIcon: {
    width: "100%",
    height: "100%",
    display: "block",

    transition:
      "fill .18s ease",
  },

  ratingLabelWrap: {
    minHeight: 22,
    marginTop: 16,
  },

  ratingLabel: {
    color: COLORS.text,
    fontSize: 13.5,
    lineHeight: 1.3,
    fontWeight: 800,
    letterSpacing: "-0.02em",
  },

  ratingHint: {
    color: "#999999",
    fontSize: 12,
    lineHeight: 1.3,
    fontWeight: 600,
  },

  textarea: {
    width: "100%",
    maxWidth: 500,
    minHeight: 112,
    marginTop: 21,
    padding: "15px 16px",
    resize: "none",
    boxSizing: "border-box",
    border:
      `1px solid ${COLORS.border}`,
    borderRadius: 8,
    outline: "none",
    background: "#FAFAFA",
    color: COLORS.text,
    fontFamily: "inherit",
    fontSize: 14,
    lineHeight: 1.48,
    fontWeight: 500,
    letterSpacing: "-0.015em",
    textAlign: "left",
  },

  submissionError: {
    width: "100%",
    maxWidth: 500,
    marginTop: 10,
    color: "#B42318",
    fontSize: 12.5,
    lineHeight: 1.4,
    fontWeight: 650,
    letterSpacing: "-0.01em",
    textAlign: "left",
  },

  connectionsVisual: {
    position: "relative",
    width: 230,
    height: 170,
    marginBottom: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  chatBubble: {
    position: "absolute",
    top: 4,
    left: "50%",
    width: 92,
    height: 92,
    display: "grid",
    placeItems: "center",
    borderRadius: 30,
    background: "#FFD84D",
    color: COLORS.text,
    transform: "translateX(-50%)",
    boxShadow:
      "0 18px 36px rgba(15,23,42,0.12)",
  },

  avatarCluster: {
    position: "absolute",
    left: "50%",
    bottom: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateX(-50%)",
  },

 

  connectionsText: {
    maxWidth: 420,
    margin: "18px auto 0",
    color: COLORS.subtext,
    fontSize: 15,
    lineHeight: 1.5,
    fontWeight: 500,
    letterSpacing: "-0.02em",
  },

  footer: {
    width: "100%",
    padding: "11px 18px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    background:
      "rgba(255,255,255,0.97)",
    boxSizing: "border-box",
    flexShrink: 0,
  },

  stepDots: {
  height: 6,
  marginTop: 18,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
},

  stepDot: {
    height: 5,
    borderRadius: 999,
    background: COLORS.text,
  },

  primaryButton: {
    width: "100%",
    maxWidth: 520,
    minHeight: 50,
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    border: 0,
    borderRadius: 12,
    background: COLORS.text,
    color: "#ffffff",
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "-0.015em",
    cursor: "pointer",
  },

  primaryButtonDisabled: {
    opacity: 0.28,
    cursor: "not-allowed",
  },

  
};